// File: src/types/assignment.ts

// Assignment types for the quartermaster system

export interface Assignment {
  tenantId: string;           // Partition key
  assignmentId: string;       // Sort key
  inventoryItemId: string;    // Links to inventory item
  itemName: string;          // For display purposes
  itemSerialNumber?: string;  // Only for serialized items (fixed field name)
  assignedToUserId: string;   // Links to user record
  assignedToUserName: string; // For display purposes (fixed field name)
  assignedToUserBadge: string; // Badge number
  assignedByUserId: string;   // Who made the assignment (fixed field name)
  assignedByUserName?: string; // Name of who made the assignment
  assignmentDate: string;     // ISO string (fixed field name)
  assignmentReason: string;   // 'Initial Issue', 'Replacement-Lost', etc.
  status: string;            // 'Active', 'Returned', 'Lost', 'Destroyed'
  
  // Return information (when applicable) - FIXED: Match database field names
  returnedAt?: string;       // FIXED: Was returnDate, now matches database field
  returnedBy?: string;       // FIXED: Was returnedByUserId, now matches database field
  returnCondition?: string;  // 'Good', 'Damaged', 'Lost'
  returnNotes?: string;
  
  // Standard audit fields
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssignmentRequest {
  assignment: {
    inventoryItemId: string;
    assignedToUserId: string;
    assignmentReason?: string;
    notes?: string;
  };
}

export interface ReturnAssignmentRequest {
  condition: 'Good' | 'Damaged' | 'Lost';
  notes?: string;
}

export interface AssignmentFilters {
  status?: 'Active' | 'Returned' | 'Lost' | 'Destroyed' | 'all';
  assignedToUserId?: string;
  inventoryItemId?: string;
  assignmentReason?: string;
  dateFrom?: string;
  dateTo?: string;
  dateRange?: {           // Added for UI compatibility
    start: string;
    end: string;
  };
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface AssignmentListResponse {
  assignments: Assignment[];
  lastEvaluatedKey?: string;
  count: number;
  total: number;
  totalCount?: number;     // Added for UI compatibility
  hasMore?: boolean;       // Added for UI compatibility
}

export interface OfficerEquipmentResponse {
  assignments: Assignment[];
  officerName: string;
  totalItems: number;
}

export interface ItemAssignmentHistory {
  assignments: Assignment[];
  itemName: string;
  currentStatus: string;
  totalAssignments: number;
}

// Assignment reasons enum
export const ASSIGNMENT_REASONS = {
  INITIAL_ISSUE: 'Initial Issue',
  REPLACEMENT_LOST: 'Replacement-Lost',
  REPLACEMENT_DAMAGED: 'Replacement-Damaged',
  PROMOTION: 'Promotion',
  TRANSFER: 'Transfer',
  TEMPORARY: 'Temporary',
  TRAINING: 'Training'
} as const;

export type AssignmentReason = typeof ASSIGNMENT_REASONS[keyof typeof ASSIGNMENT_REASONS];

// Assignment status enum
export const ASSIGNMENT_STATUS = {
  ACTIVE: 'Active',
  RETURNED: 'Returned',
  LOST: 'Lost',
  DESTROYED: 'Destroyed'
} as const;

export type AssignmentStatus = typeof ASSIGNMENT_STATUS[keyof typeof ASSIGNMENT_STATUS];

// Return condition enum
export const RETURN_CONDITIONS = {
  GOOD: 'Good',
  DAMAGED: 'Damaged',
  LOST: 'Lost'
} as const;

export type ReturnCondition = typeof RETURN_CONDITIONS[keyof typeof RETURN_CONDITIONS];

// Equipment category icons for UI display
export const EQUIPMENT_CATEGORY_ICONS: Record<string, string> = {
  'Firearms': '🔫',
  'Protective Equipment': '🛡️',
  'Communication': '📻',
  'Duty Belt Equipment': '⚖️',
  'Uniform Items': '👕',
  'Vehicle Equipment': '🚗',
  'Office Supplies': '📋',
  'Supplies': '🔋'
};

// API response types
export interface AssignmentAPIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// Error codes for assignment operations
export const ASSIGNMENT_ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INVALID_REQUEST: 'INVALID_REQUEST',
  ASSIGNMENT_NOT_FOUND: 'ASSIGNMENT_NOT_FOUND',
  ITEM_NOT_FOUND: 'ITEM_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  ITEM_NOT_AVAILABLE: 'ITEM_NOT_AVAILABLE',
  INSUFFICIENT_QUANTITY: 'INSUFFICIENT_QUANTITY',
  ASSIGNMENT_NOT_ACTIVE: 'ASSIGNMENT_NOT_ACTIVE',
  DB_ERROR: 'DB_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR'
} as const;

export type AssignmentErrorCode = typeof ASSIGNMENT_ERROR_CODES[keyof typeof ASSIGNMENT_ERROR_CODES];

// Form data types for UI components
export interface AssignmentFormData {
  inventoryItemId: string;
  assignedToUserId: string;
  assignmentReason: AssignmentReason;
  notes: string;
  confirmFirearmPolicy?: boolean; // For firearm assignments
}

export interface ReturnFormData {
  condition: ReturnCondition;
  notes: string;
  quantity?: number;        // ✅ ADD: For bulk item returns
  returnToLocation?: string; // ✅ ADD: Return location
}

// Assignment summary for dashboard/statistics
export interface AssignmentSummary {
  totalActive: number;
  totalReturned: number;
  totalLost: number;
  totalDestroyed: number;
  recentAssignments: Assignment[];
  itemsNeedingReturn: Assignment[];
  overdueReturns: Assignment[];
}

// Officer equipment summary
export interface OfficerEquipmentSummary {
  officerId: string;
  officerName: string;
  activeAssignments: number;
  totalValue: number;
  hasFirearms: boolean;
  lastAssignment?: string;
  assignments: Assignment[];
}

// Bulk assignment operations
export interface BulkAssignmentRequest {
  assignments: CreateAssignmentRequest[];
  validateOnly?: boolean;
}

export interface BulkAssignmentResponse {
  successful: Assignment[];
  failed: Array<{
    request: CreateAssignmentRequest;
    error: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

// Search and filter types
export interface AssignmentSearchParams {
  query?: string;           // Search across item names, officer names, serial numbers
  status?: AssignmentStatus[];
  assignmentReasons?: AssignmentReason[];
  dateRange?: {
    start: string;
    end: string;
  };
  officers?: string[];      // Array of officer IDs
  items?: string[];        // Array of item IDs
  categories?: string[];   // Array of item categories
  sortBy?: 'assignmentDate' | 'returnDate' | 'itemName' | 'officerName';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Assignment analytics
export interface AssignmentAnalytics {
  totalAssignments: number;
  activeAssignments: number;
  completionRate: number;
  averageAssignmentDuration: number;
  topOfficersByEquipment: Array<{
    officerId: string;
    officerName: string;
    equipmentCount: number;
  }>;
  topItemsByAssignments: Array<{
    itemId: string;
    itemName: string;
    assignmentCount: number;
  }>;
  assignmentsByReason: Record<AssignmentReason, number>;
  assignmentsByStatus: Record<AssignmentStatus, number>;
  monthlyTrends: Array<{
    month: string;
    assignments: number;
    returns: number;
  }>;
}

// ADDITIONAL TYPES FOR UI COMPONENT COMPATIBILITY
// These bridge your existing types with the UI components

export interface AssignmentRequest {
  assignedToUserId: string;
  assignedToName: string;
  assignedBy: string;
  assignedDate: string;
  assignmentReason: string;
  notes?: string;
  quantity?: number;
}

export interface ReturnRequest {
  condition?: string;        // For serialized items
  quantity?: number;         // For bulk items
  returnToLocation?: string; // Updated field name for compatibility
  returnedBy?: string;
  notes?: string;
}

export interface UpdateAssignmentRequest {
  assignmentReason?: string;
  status?: string;
  notes?: string;
}

// Officer equipment for UI components
export interface OfficerEquipment {
  assignment: Assignment;
  inventoryItem?: any;
  expirationDate?: string;
  expirationStatus?: 'Valid' | 'Warning' | 'Expired';
}

// For compatibility with inventory system
export interface InventoryItemForAssignment {
  itemId: string;
  itemName: string;
  isSerialNumberRequired: boolean;
  serialNumber?: string;
  availableQuantity: number;
  category: string;
  status: string;
}